import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import {
  Box,
  Card,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { IconComponent } from '@zippeditoolsjs/zippedi-icons';

import { RobotViewContext } from '../../context/RobotView';
import LastRefresh from '../tools/LastRefresh';
import { toTitleCase } from '../tools/utils';
import RobotStatusModal from './Components/RobotStatusModal';
import RobotStatusWidget from './RobotStatusWidget';

export default function NavigationWidget(props) {
  const {
    title,
    mqttNavInfo,
    robot,
    store,
    robotMainStatus,
    robotDetailStatus,
    robotStatusHistory,
    getRobotStatusHistory,
    isLoadingRobotStatusHistory,
    activeAlerts,
    robotState,
    isNoRobotSelected,
    rawData,
  } = props;
  const { t } = useTranslation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const [connected, setConnected] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [lastStatusTime, setLastStatusTime] = useState(null);

  const {
    connectionStatusMsg,
    rawRobotStatus,
  } = useContext(RobotViewContext);

  // Hooks
  useEffect(() => {
    if (rawRobotStatus) {
      setLastStatusTime(new Date());
    }
  }, [rawRobotStatus]);

  // Since the activeAlerts object is not big, this will do, but
  // caution is advised since mediumr objects could affect performance.
  useEffect(() => {
    let connectionAlert = activeAlerts.find(
      (alert) => alert?.alert_id === 'no-connection'
    );
    if (connectionAlert) setConnected(false);
    else if (connectionStatusMsg.mqtt_connection) setConnected(connectionStatusMsg.mqtt_connection);
  }, [JSON.stringify(activeAlerts), connectionStatusMsg]);

  // Handlers
  const handleColorRobotState = (robotState) => {
    let style;
    switch (robotState) {
      case 'active':
        style = {
          backgroundColor: '#0E7F19',
          color: 'white',
        };
        break;

      case 'not-operational':
        style = {
          backgroundColor: '#fe2d38',
          color: 'white',
        };
        break;
      case 'under-maintenance':
        style = {
          backgroundColor: '#FFA500',
          color: 'white',
        };
        break;
      default:
        style = {
          backgroundColor: '#808080',
          color: 'white',
        };
        break;
    }
    return style;
  };

  // card with widget info
  return (
    <>
      {/* "See more" modal */}
      <RobotStatusModal
        open={openModal}
        setOpen={setOpenModal}
        data={robotDetailStatus}
        robotCode={`${store}-${robot}`}
        robotStatusHistory={robotStatusHistory}
        getRobotStatusHistory={getRobotStatusHistory}
        isLoadingRobotStatusHistory={isLoadingRobotStatusHistory}
      />
      {!isNoRobotSelected ? (
        <Card component={Stack} direction={isSmallScreen ? 'column' : 'row'} divider={<Divider orientation={isSmallScreen ? 'horizontal' : 'vertical'} flexItem />}>
          {/* Title, "see more" button and refresh */}
          <Stack p={2} justifyContent='center'>
            <Stack direction='row' spacing={0.5} justifyContent='center' alignItems='center'>
              {/* Status title */}
              <Typography sx={{ fontWeight: 'bold' }}>{title}</Typography>
              {/* Status icon */}
              {robotMainStatus === 'OK' ? (
                <CheckCircleIcon sx={{ fontSize: '20px', color: '#0E7F19' }} />
              ) : robotMainStatus === 'Error' ? (
                <CancelIcon color="error" sx={{ fontSize: '20px' }} />
              ) : robotMainStatus === 'Warning' ? (
                <ErrorIcon color="warning" sx={{ fontSize: '20px' }} />
              ) : (
                <CircularProgress size={14} />
              )}
              {/* "See more" button */}
              <IconButton
                size="small"
                onClick={() => setOpenModal(true)}
              >
                <Tooltip title={t('overseer_app.widget.see_more', 'See More')}>
                  <Typography>
                    <IconComponent
                      iconName={'open-outline'}
                      style={{ fontSize: "20px" }}
                    />
                  </Typography>
                </Tooltip>
              </IconButton>
            </Stack>
            {/* Last refresh */}
            <LastRefresh lastUpdateTime={lastStatusTime} timeInterval={10000} />
          </Stack>
          {/* Status summary */}
          <Box sx={{ display: 'flex', flexGrow: 1, overflow: 'auto' }}>
            <RobotStatusWidget
              mqttNavInfo={mqttNavInfo}
              isHeaderInfo={true}
            />
          </Box>
          {/* Robot state chips */}
          <Stack direction={isSmallScreen ? 'column' : 'row'} spacing={1} p={2} justifyContent="center" alignItems="center">
            <Chip
              label={<Typography variant="overline">{robotState ? t(`overseer_app.general.${robotState}`, toTitleCase(robotState)) : t('No info')}</Typography>}
              size="small"
              variant="filled"
              style={handleColorRobotState(robotState)}
            />
            <Chip
              label={<Typography variant="overline">{connected ? t('Connected') : t('Disconnected')}</Typography>}
              size="small"
              variant="filled"
              style={
                connected
                  ? {
                    backgroundColor: '#0E7F19',
                    color: 'white',
                  }
                  : {
                    backgroundColor: '#fe2d38',
                    color: 'white',
                  }
              }
            />
          </Stack>
        </Card>
      ) : (
        <Stack direction='row' alignItems='center' divider={<Divider orientation="vertical" flexItem />}>
          <Typography align='center' sx={{ fontWeight: 'bold', px: '4em', }}>{title}</Typography>
          <Typography variant="body2" color="text.secondary" align='center' sx={{ flexGrow: 1 }}>
            {t('overseer_app.general.no_robot_selected', 'No robot selected')}
          </Typography>
        </Stack>
      )}
    </>
  );
}
